import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useEffect, useState } from "react";
import ModalShell from "../ModalShell";
import styles from "./style.module.scss";

export interface ReceiptView {
  /** The transaction the receipt belongs to, shown under the title. */
  transactionId: string;
  /** S3 image the school uploaded. Absent when none was attached. */
  image?: string;
  /** Why the payment was rejected. The API only sets it on a rejection. */
  reason?: string;
  status?: string;
}

interface ReceiptModalProps {
  open: boolean;
  receipt?: ReceiptView;
  onClose: () => void;
  title?: string;
}

/** The uploaded receipt image on its own. The reason has its own dialog. */
function ReceiptModal({
  open,
  receipt,
  onClose,
  title = "Payment receipt",
}: ReceiptModalProps) {
  /** The S3 link can 404 or expire; fall back to a message, not a broken icon. */
  const [failed, setFailed] = useState(false);

  // A new receipt deserves a fresh attempt at loading its image.
  useEffect(() => {
    setFailed(false);
  }, [receipt?.image]);

  if (!receipt) return null;

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={title}
      subtitle={receipt.transactionId}
    >
      <div className={styles.imageWrap}>
        {receipt.image && !failed ? (
          <img
            src={receipt.image}
            alt={`Receipt for ${receipt.transactionId}`}
            className={styles.image}
            onError={() => setFailed(true)}
          />
        ) : (
          <div className={styles.noImage}>
            {receipt.image
              ? "This receipt image could not be loaded."
              : "No receipt was attached to this payment."}
          </div>
        )}
      </div>

      {receipt.image && (
        <a
          href={receipt.image}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.openFull}
        >
          <OpenInNewIcon className={styles.openIcon} />
          Open full size
        </a>
      )}
    </ModalShell>
  );
}

export default ReceiptModal;
